// Centralised content for UKTDA.
// UK English spellings retained throughout.

export const COMPANY = {
  legalName: "UK Train Driver Academy Ltd",
  shortName: "UK Train Driver Academy",
  abbreviation: "UKTDA",
  email: "info@traindriveracademy.com",
  tagline: "Developing safer, more capable railway professionals.",
};

export const BRANDS = [
  {
    slug: "train-driver-foundation",
    name: "Train Driver Foundation",
    eyebrow: "Candidate development",
    url: "https://www.traindriverfoundation.com",
    logo: "https://customer-assets.emergentagent.com/job_rail-academy-preview/artifacts/q05leeaq_logo%20New.jpg",
    summary:
      "Supports aspiring train drivers with structured preparation for CVs, interviews, psychometric tests, situational judgement, operational thinking and early-stage railway career development.",
    points: [
      "CV and application support",
      "Interview and SJT preparation",
      "Operational thinking foundations",
      "Career readiness coaching",
    ],
  },
  {
    slug: "train-driver-psychometrics",
    name: "Train Driver Psychometrics",
    eyebrow: "Psychometric practice tools",
    url: "https://traindriverpsychometrics.co.uk",
    logo: "https://customer-assets.emergentagent.com/job_rail-academy-preview/artifacts/4lbop1ia_TDF%20Psychometrics%20512.png",
    summary:
      "Provides RIS-3751-TOM aligned psychometric preparation. Ethical practice resources, performance tracking and test familiarisation for aspiring train drivers.",
    points: [
      "RIS-3751-TOM aligned content",
      "Test familiarisation and timing",
      "Performance tracking",
      "Targeted skill drills",
    ],
  },
  {
    slug: "railway-careers-uk",
    name: "Railway Careers UK",
    eyebrow: "Careers information platform",
    url: "https://railwaycareers.co.uk",
    logo: "https://customer-assets.emergentagent.com/job_rail-academy-preview/artifacts/ogy2xcz9_Main%20RailwayCareers%20Logo.png",
    summary:
      "A railway careers information platform helping candidates understand operator recruitment, trainee driver opportunities, career pathways and rail industry roles.",
    points: [
      "Operator recruitment insight",
      "Trainee driver opportunities",
      "Career pathway mapping",
      "Industry role explainers",
    ],
  },
];

export const TRAINING_CATEGORIES = [
  {
    code: "01",
    title: "Safety Critical Competence Development",
    summary:
      "Structured development of operational safety-critical employees. Assessment cyles, post incident development, risk areas and reflective practice.",
  },
  {
    code: "02",
    title: "Train Driver Instructor Development",
    summary:
      "Capability development for driver instructors: coaching technique, assessment integrity, evidence-based feedback and learner progression.",
  },
  {
    code: "03",
    title: "Driver Manager Development",
    summary:
      "Building operational leadership: managing competence, post-incident support, performance conversations and risk-based decision making.",
  },
  {
    code: "04",
    title: "Incident Investigation Awareness",
    summary:
      "Introduces structured investigation thinking, contributory factors, human performance and organisational learning principles.",
  },
  {
    code: "05",
    title: "Non-Technical Skills (NTS)",
    summary:
      "Situational awareness, diligence, communication, decision making and action, cooperation with others, workload management and self-management applied to safety-critical roles.",
  },
  {
    code: "06",
    title: "Human Factors",
    summary:
      "Understanding how people, equipment, environment and procedures interact — and how to design for reliable human performance.",
  },
  {
    code: "07",
    title: "Fatigue and Alertness",
    summary:
      "Fatigue science, alertness strategies, rostering considerations and the human cost of operational fatigue.",
  },
  {
    code: "08",
    title: "Operational Thinking",
    summary:
      "Developing the operational mindset required to interpret rules, anticipate risk and act under pressure.",
  },
  {
    code: "09",
    title: "Risk-Based Decision Making",
    summary:
      "Frameworks for assessing risk in real time, balancing operational pressures with safety-critical judgement.",
  },
  {
    code: "10",
    title: "Rules, Standards & Professional Behaviours",
    summary:
      "Working with industry rules and standards, professional conduct expectations and behavioural safety culture.",
  },
  {
    code: "11",
    title: "Safety Critical Recruitment",
    summary:
      "Supporting operators with an evidence-based approach to structured interviews and situational judgement exercises.",
  },
  {
    code: "12",
    title: "Safety-Critical Communication",
    summary:
      "Clear, accurate, challenge-and-confirm communication for cab, control, dispatch and incident scenarios.",
  },
  {
    code: "13",
    title: "Performance Improvement & Reflective Practice",
    summary:
      "Reflective tools, self-evaluation, deliberate practice and structured improvement plans for safety-critical roles.",
  },
];

export const PD_AREAS = [
  {
    title: "Non-Technical Skills",
    body:
      "NTS underpin safe performance in safety-critical roles. We develop situational awareness, decision making, communication, leadership and teamworking through scenario-led learning grounded in railway operations.",
  },
  {
    title: "Human Factors",
    body:
      "Human factors explains how people interact with rules, equipment, environment and one another. Strong human factors capability reduces error, improves design, and supports a just, fair and learning-oriented safety culture.",
  },
  {
    title: "Fatigue and Workload",
    body:
      "Fatigue is a recognised safety risk in the rail sector. We explore fatigue science, rostering pressures, recovery, individual strategies and organisational responsibilities.",
  },
  {
    title: "Operational Decision Making",
    body:
      "Operational decisions are rarely clean. We develop the structured thinking, risk awareness and professional judgement required to act safely under uncertainty and time pressure.",
  },
  {
    title: "Communication and Escalation",
    body:
      "Safety-critical communication is precise, challenge-tolerant and timely. We strengthen radio discipline, handovers, escalation behaviours and challenge-and-confirm habits.",
  },
  {
    title: "Incident Investigation and Learning",
    body:
      "Effective learning from incidents requires more than blame. We introduce structured investigation thinking, contributory factor analysis and organisational learning principles.",
  },
  {
    title: "Competence Management",
    body:
      "Competence is more than a tick-box. We work with operators on competence frameworks, evidence quality, assessor calibration and behavioural standards.",
  },
  {
    title: "Instructor and Manager Capability",
    body:
      "The capability of instructors and managers shapes the capability of drivers. We develop coaching, feedback, assessment integrity and operational leadership.",
  },
];

export const HOMEPAGE_VALUES = [
  {
    label: "Safety-critical learning",
    body: "Every programme is grounded in safety-critical thinking, not generic training.",
  },
  {
    label: "Operational thinking",
    body: "We develop the judgement, awareness and communication habits that real railway roles demand.",
  },
  {
    label: "Human factors and NTS",
    body: "Embedded throughout — because how people perform matters as much as what they know.",
  },
  {
    label: "Competence development",
    body: "Structured, evidence-based development for individuals, teams and organisations.",
  },
  {
    label: "Wider recruitment and career preparation",
    body:
      "Helping candidates understand the behaviours, standards and safety-critical expectations associated with railway recruitment.",
  },
  {
    label: "Support for organisations",
    body: "Bespoke design for operators, training providers, heritage railways and operational teams.",
  },
];

// Page-specific FAQ sets, written to target high-intent search queries
// for each page's topic. Each is wrapped with FAQPage JSON-LD by FAQSection.

export const FAQS_HOME = [
  {
    q: "What is UKTDA?",
    a: 'UKTDA is a specialist railway training and professional development company. It is the parent organisation of <a href="/our-brands">Train Driver Foundation, Train Driver Psychometrics and Railway Careers UK</a>, and delivers training, competence development and recruitment preparation for individuals and rail-sector organisations across the UK.',
  },
  {
    q: "Is UKTDA the parent company of Train Driver Foundation?",
    a: 'Yes. <a href="/our-brands">Train Driver Foundation, Train Driver Psychometrics and Railway Careers UK</a> are all brands of UKTDA. Each brand serves a distinct part of the railway career journey — preparation, psychometric practice and careers information.',
  },
  {
    q: "What services does UKTDA offer?",
    a: 'UKTDA delivers <a href="/for-individuals">train driver preparation</a>, psychometric test preparation, careers information, <a href="/training">professional railway training</a>, instructor and driver manager development, <a href="/professional-development">human factors and non-technical skills (NTS) workshops</a>, fatigue and competence support, and <a href="/for-organisations">bespoke training programmes for railway organisations</a>.',
  },
  {
    q: "Do you support both individual learners and railway organisations?",
    a: 'Yes. Individual aspiring and developing train drivers are supported through our <a href="/our-brands">specialist brands</a>; railway operators, training providers and heritage railways access <a href="/for-organisations">bespoke training and professional development</a> directly through UKTDA.',
  },
  {
    q: "Where is UKTDA based?",
    a: 'UKTDA is registered in England and Wales and serves the UK rail sector. Training can be delivered remotely, at client sites, and through digital learning resources depending on the programme. <a href="/contact">Contact us</a> to discuss requirements.',
  },
  {
    q: "Do your courses replace employer training or formal certification?",
    a: "No. UKTDA provides preparation, education, training and professional development. Our courses and resources do not replace employer training, formal competence assessment, licensing, certification, medical assessment, psychometric assessment or authorised operational instruction.",
  },
];

export const FAQS_ABOUT = [
  {
    q: "Why was UKTDA founded?",
    a: 'UKTDA was founded to bring together specialist railway career preparation and professional training under one credible, safety-focused parent brand — helping individuals enter the cab better prepared and helping organisations strengthen safety-critical capability. <a href="/our-brands">Learn about our brands</a>.',
  },
  {
    q: "What makes UKTDA different from other rail training providers?",
    a: 'UKTDA focuses specifically on safety-critical railway roles, with <a href="/professional-development">human factors, non-technical skills, operational thinking and competence development</a> embedded throughout. We do not position ourselves as a generic e-learning provider; our work is grounded in operational reality.',
  },
  {
    q: "What does safety-critical training mean?",
    a: 'Safety-critical training is learning designed for roles where errors can lead to harm — for example, <a href="/for-individuals">train drivers</a>, instructors and operational managers. It blends technical knowledge with <a href="/glossary#nts">non-technical skills</a>, situational awareness, decision making and communication.',
  },
  {
    q: "Who is UKTDA for?",
    a: 'UKTDA supports <a href="/for-individuals">aspiring and developing train drivers</a>, instructors and driver managers, plus <a href="/for-organisations">rail-sector organisations</a> including operators, training providers and heritage railways.',
  },
  {
    q: "Is UKTDA an accredited training organisation?",
    a: "UKTDA is registered on the UKRLP and delivers preparation, education, training and professional development. We are not a licensing body and our programmes do not replace formal employer competence assessment, certification or authorised operational instruction.",
  },
];

export const FAQS_BRANDS = [
  {
    q: "What is Train Driver Foundation?",
    a: 'Train Driver Foundation (TDF) is the UKTDA brand supporting aspiring train drivers with structured preparation for CVs, interviews, <a href="/glossary#psychometric">psychometric tests</a>, situational judgement exercises and <a href="/glossary#operational-thinking">operational thinking</a> — the areas rail operators commonly assess during trainee driver recruitment. <a href="https://www.traindriverfoundation.com" rel="noopener noreferrer" target="_blank">Visit the TDF website</a>.',
  },
  {
    q: "What is Train Driver Psychometrics?",
    a: 'Train Driver Psychometrics is the UKTDA brand offering <a href="/glossary#ris-3751-tom">RIS-3751-TOM aligned</a> ethical psychometric preparation tools, practice tests, performance tracking and test familiarisation for aspiring train drivers in the UK. <a href="https://traindriverpsychometrics.co.uk" rel="noopener noreferrer" target="_blank">Visit Train Driver Psychometrics</a>.',
  },
  {
    q: "What is Railway Careers UK?",
    a: 'Railway Careers UK is the UKTDA careers information platform helping candidates understand operator recruitment, trainee driver opportunities, career pathways and the wider range of UK railway roles. <a href="https://railwaycareers.co.uk" rel="noopener noreferrer" target="_blank">Visit Railway Careers UK</a>.',
  },
  {
    q: "How do the UKTDA brands work together?",
    a: "Each brand addresses a different part of a candidate's journey: Railway Careers UK helps with discovery, Train Driver Foundation supports candidate preparation, Train Driver Psychometrics provides practice tools and TRAC supports train drivers and other safety critical roles once recruited. UKTDA sits above them all as the parent company delivering professional and organisational training.",
  },
  {
    q: "Can I use more than one UKTDA brand at the same time?",
    a: "Yes. Candidates may use Railway Careers UK to understand the industry, Train Driver Foundation to prepare applications and interviews, and Train Driver Psychometrics to practise assessment-style skills. The brands are designed to complement each other.",
  },
];

export const FAQS_TRAINING = [
  {
    q: "What training courses do you offer for train drivers?",
    a: 'Our training spans <a href="/training">train driver competence development, instructor development, driver manager development, non-technical skills (NTS), human factors, fatigue and alertness, operational thinking, risk-based decision making, safety-critical communication, and recruitment preparation</a>.',
  },
  {
    q: "Do you provide non-technical skills (NTS) training for train drivers?",
    a: 'Yes. <a href="/professional-development">NTS development</a> is a core part of our offering — covering situational awareness, communication, decision making, workload management and teamwork, all applied in railway operational contexts. See our <a href="/glossary#nts">glossary entry on NTS</a>.',
  },
  {
    q: "What is human factors training in the rail industry?",
    a: '<a href="/glossary#human-factors">Human factors</a> training explores how people interact with rules, equipment, environment and one another. Strong human factors capability can reduce error, improve the design of work and procedures, and support a fair, learning-oriented safety culture. See our <a href="/professional-development">Professional Development</a> page for more.',
  },
  {
    q: "Can training be delivered at my organisation's site?",
    a: 'Yes. Programmes can be delivered for individuals, teams or whole organisations — remotely, on client sites, fully online or as blended packages. We tailor delivery to your operational context. <a href="/for-organisations">Discuss organisational training</a>.',
  },
  {
    q: "How long are your courses?",
    a: "Length depends on the programme. Awareness sessions may be delivered as short focused sessions, while competence and instructor development packages typically run as multi-session programmes over several weeks. We scope length around your objectives.",
  },
  {
    q: "Are your courses aligned to RSSB / RIS standards?",
    a: 'Our train driver psychometric preparation aligns to <a href="/glossary#ris-3751-tom">RIS-3751-TOM</a>. Other programmes are designed with reference to relevant industry standards and good practice where appropriate, but they support — not replace — employer formal competence assessment.',
  },
  {
    q: "Do your courses provide a certificate?",
    a: "We provide attendance and completion documentation for professional development programmes where appropriate. These programmes are continuing professional development (CPD) in nature and are not a substitute for employer competence certification or operational licensing.",
  },
];

export const FAQS_INDIVIDUALS = [
  {
    q: "How do I become a train driver in the UK?",
    a: 'Most UK train operators recruit trainee drivers through structured campaigns: application, psychometric assessment, structured interview, medical and competence assessment. Becoming a competent driver then usually involves operator-led training, route learning, traction training and assessment. <a href="/for-individuals">See our preparation support</a>.',
  },
  {
    q: "How hard is the train driver psychometric test?",
    a: 'The standard UK train driver psychometric battery aligned to <a href="/glossary#ris-3751-tom">RIS-3751-TOM</a> is demanding because it tests attention, concentration, reaction speed, rule following and safety-critical judgement under pressure. Familiarisation and structured practice can help candidates understand the test format, timing and skill demands. <a href="https://traindriverpsychometrics.co.uk" rel="noopener noreferrer" target="_blank">Train Driver Psychometrics</a> provides structured practice resources for this purpose.',
  },
  {
    q: "What is RIS-3751-TOM?",
    a: '<a href="/glossary#ris-3751-tom">RIS-3751-TOM</a> is the Rail Industry Standard that defines the train driver selection psychometric assessment process used by UK operators. It sets out the cognitive and behavioural assessment requirements used to assess suitability for safety-critical driving roles. Practice tools are available at <a href="https://traindriverpsychometrics.co.uk" rel="noopener noreferrer" target="_blank">Train Driver Psychometrics</a>.',
  },
  {
    q: "Do I need a degree to become a train driver?",
    a: "No. A degree is not usually required to become a UK train driver. Operators are more interested in safety-critical thinking, reliability, communication, concentration, rule discipline and the ability to pass the required selection and medical stages.",
  },
  {
    q: "What age can you become a train driver in the UK?",
    a: 'The minimum age for train drivers in Great Britain is changing to 18 from 30 June 2026. Candidates must still meet the required medical, competence, training and selection standards. Our <a href="/future-driver">Future Driver pathway</a> supports younger applicants preparing for the role.',
  },
  {
    q: "How should I prepare for a train driver interview?",
    a: 'Train operator interviews are typically structured and behavioural. Prepare evidence-based examples covering safety mindset, reliability, communication, decision making under pressure, rule following and learning from mistakes. <a href="https://www.traindriverfoundation.com" rel="noopener noreferrer" target="_blank">Train Driver Foundation</a> provides structured interview preparation specific to UK train driver recruitment.',
  },
  {
    q: "How much do trainee train drivers earn in the UK?",
    a: "Trainee salaries vary by operator and location. Fully qualified driver salaries also vary depending on operator, agreement, route, shift pattern and overtime arrangements. Candidates should always check current operator adverts for accurate salary details.",
  },
  {
    q: "How long does train driver training take?",
    a: "Operator-led trainee driver training commonly takes around 12–18 months, though this varies by employer, traction, route complexity and assessment arrangements. It usually includes classroom learning, simulator work, traction handling and supervised route learning.",
  },
];

export const FAQS_ORGANISATIONS = [
  {
    q: "Do you deliver bespoke train driver training for railway organisations?",
    a: 'Yes. We design and deliver <a href="/training">bespoke programmes</a> for operators, training providers, heritage railways and operational teams — built around your routes, traction, risks, learner population and competence framework.',
  },
  {
    q: "Do you provide driver instructor and driver manager development?",
    a: 'Yes. Instructor development covers coaching technique, evidence-based feedback, assessment integrity and learner progression. Manager development covers operational leadership, performance conversations, post-incident support and <a href="/glossary#risk-based-decision-making">risk-based decision making</a>.',
  },
  {
    q: "What is competence management for train drivers?",
    a: '<a href="/glossary#competence-management">Competence management</a> is the structured system operators use to develop, assess, evidence and maintain safety-critical capability over time. We support competence framework design, assessor calibration, evidence quality and behavioural standards.',
  },
  {
    q: "How can you support post-incident learning?",
    a: 'We deliver <a href="/glossary#incident-investigation">incident investigation awareness</a>, contributory factor analysis and organisational learning workshops grounded in human factors. The focus is on learning and improvement rather than blame.',
  },
  {
    q: "Can you help with our fatigue risk management approach?",
    a: 'Yes. Our <a href="/glossary#fatigue">fatigue and alertness</a> programmes cover the science of fatigue, rostering considerations, individual recovery strategies and organisational responsibilities — applicable to drivers, controllers and operational managers.',
  },
  {
    q: "How quickly can a programme be designed and delivered?",
    a: 'It depends on scope. Some awareness sessions can be prepared quickly, while bespoke competence or instructor development programmes require scoping, design and review before delivery. We agree realistic timescales during the initial conversation. <a href="/contact">Discuss requirements</a>.',
  },
];

export const FAQS_PD = [
  {
    q: "What are non-technical skills (NTS) in railway operations?",
    a: "Non-technical skills are the cognitive, social and personal-resource skills that complement technical knowledge — including situational awareness, decision making, communication, teamwork, leadership and workload management. They are widely recognised as critical to safe performance in safety-critical roles.",
  },
  {
    q: "What is human factors in the rail industry?",
    a: '<a href="/glossary#human-factors">Human factors</a> is the discipline that studies how people interact with rules, equipment, environments and other people in operational contexts. Applied human factors helps design work, procedures and culture to support reliable human performance and reduce error.',
  },
  {
    q: "Why is fatigue management important for train drivers?",
    a: '<a href="/glossary#fatigue">Fatigue</a> degrades attention, reaction time and decision making — all critical to safe driving. Fatigue management combines fatigue science, rostering design, individual recovery strategies and organisational responsibility, and is a key topic in rail safety.',
  },
  {
    q: "What is competence management and why does it matter?",
    a: '<a href="/glossary#competence-management">Competence management</a> is the structured way an organisation develops, assesses and maintains safety-critical capability. It matters because licensing or initial certification alone does not guarantee ongoing safe performance — competence has to be supported across a career.',
  },
  {
    q: "What does instructor and driver manager capability development cover?",
    a: "It covers coaching, feedback, assessment integrity, evidence quality, performance conversations, post-incident support, and the operational leadership skills needed by instructors and managers in safety-critical environments.",
  },
];

export const FAQS_TRAC_CMS = [
  {
    q: "What is TRAC-CMS?",
    a: "TRAC-CMS is a competence management system for railway organisations. It supports competence records, assessment cycles, evidence management, risk identification and compliance reporting for drivers, instructors, managers and safety-critical teams.",
  },
  {
    q: "What can TRAC-CMS help us manage?",
    a: "TRAC-CMS brings together competence status tracking, assessment schedules, evidence records, development needs identification, risk-based decision making and compliance visibility — giving you centralised oversight of your team's competence.",
  },
  {
    q: "How does TRAC-CMS support compliance?",
    a: "TRAC-CMS helps you maintain audit-ready records, track competence cycles, identify gaps and risks, and generate compliance reporting. It supports your assurance activity and operational decision making.",
  },
  {
    q: "Can TRAC-CMS integrate with our existing systems?",
    a: "TRAC-CMS is designed to work alongside your training, learning and operational systems. Integration approaches depend on your specific systems and requirements. <a href=\"/contact\">Contact us</a> to discuss your setup.",
  },
  {
    q: "Who is TRAC-CMS for?",
    a: "TRAC-CMS is for railway operators, training providers, heritage railways and organisations managing safety-critical competence — train driver operations, instructor teams, management development and broader operational capability.",
  },
];

// Backwards-compatible default used if FAQSection is called without items.
export const FAQS = FAQS_HOME;

export const DISCLAIMER =
  "UKTDA provides preparation, education, training and professional development. Our courses and resources compliment employer training, formal competence assessment, psychometric assessment and authorised operational instruction. UKTDA is also well placed to support recruitment of safety critical employees as part of wider UK TOC/FOC recruitment initiatives.";

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/our-brands", label: "Brands" },
  { to: "/for-individuals", label: "Individuals" },
  { to: "/for-organisations", label: "Organisations" },
  { to: "/professional-development", label: "Prof. Development" },
  { to: "/communities", label: "Communities" },
  { to: "/contact", label: "Contact" },
];

export const IMAGES = {
  heroCab: "https://images.unsplash.com/photo-1715768049609-04261668a0d0",
  aboutFront: "https://images.unsplash.com/photo-1725540308474-c845ef7d4b93",
  generalTraining: "https://images.unsplash.com/photo-1766074582937-6456742cf799",
  routeMap: "https://images.unsplash.com/photo-1772696572566-9a3b66cd8f2d",
};